import { usePrevProjDataByLocCodeAndOptions } from "api/features/epidemiology/queries";
import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { PrevProjDataOptions } from "@/data-types/api/epidemiologyData.types";
import { INITIAL_LOC_CODE } from "config/config";
import { getInitialValues } from "layouts/dashboards/prevProj/schemas/initialValues";
import { getQueryErrorMessage } from "api/utils/queryError";

const emptyDefaults = getInitialValues(INITIAL_LOC_CODE);

const formPrevProjContext = createContext<FormPrevProjContextProps>({
  locCode: INITIAL_LOC_CODE,
  isLoading: false,
  isFormLoading: false,
  isError: false,
  errorMessage: "",
  refetch: () => {},

  countryDefaults: emptyDefaults,
  liveFormValues: emptyDefaults,
  appliedOptions: null,

  countryData: {},

  setLocCode: () => {},
  setAppliedOptions: () => {},
});

interface FormPrevProjContextProps {
  locCode: number;
  isLoading: boolean;
  isFormLoading: boolean;
  isError: boolean;
  errorMessage: string;
  refetch: () => void;

  /** Schema defaults for the selected country (PrevProjForm init). */
  countryDefaults: PrevProjDataOptions;
  /** Values for export: appliedOptions ?? countryDefaults. */
  liveFormValues: PrevProjDataOptions;
  /** Last submitted values — drives the prevalence-projection query. */
  appliedOptions: PrevProjDataOptions | null;

  countryData: any;

  setLocCode: (locCode: number) => void;
  setAppliedOptions: (options: PrevProjDataOptions | null) => void;
}

const FormPrevProjContextProvider = ({ children }: { children: ReactNode }): JSX.Element => {
  const [locCode, setLocCodeState] = useState<number>(INITIAL_LOC_CODE);
  const [appliedOptions, setAppliedOptions] = useState<PrevProjDataOptions | null>(null);

  const setLocCode = useCallback((nextLocCode: number) => {
    setLocCodeState(Number(nextLocCode));
  }, []);

  const {
    data: countryData,
    isLoading,
    isError,
    error,
    refetch,
  } = usePrevProjDataByLocCodeAndOptions(locCode, appliedOptions);
  const errorMessage = getQueryErrorMessage(error);

  const hasLoadedOnceRef = useRef(false);
  if (!isLoading) hasLoadedOnceRef.current = true;
  const isFormLoading = isLoading && !hasLoadedOnceRef.current;

  const countryDefaults = useMemo(() => getInitialValues(locCode), [locCode]);

  const liveFormValues = useMemo(
    () => appliedOptions ?? countryDefaults,
    [appliedOptions, countryDefaults]
  );

  const locCodeEffectAttempt = useRef(true);
  useEffect(() => {
    if (locCodeEffectAttempt.current) {
      locCodeEffectAttempt.current = false;
      return;
    }
    setAppliedOptions(null);
  }, [locCode]);

  // Drop legacy draft session key from the previous currentOptionsPrevBr system.
  useEffect(() => {
    sessionStorage.removeItem("currentOptionsPrevBr");
  }, []);

  const value = useMemo(
    () => ({
      locCode,
      isLoading,
      isFormLoading,
      isError,
      errorMessage,
      refetch,

      countryDefaults,
      liveFormValues,
      appliedOptions,

      countryData,

      setLocCode,
      setAppliedOptions,
    }),
    [
      locCode,
      isLoading,
      isFormLoading,
      isError,
      errorMessage,
      refetch,
      countryDefaults,
      liveFormValues,
      appliedOptions,
      countryData,
      setLocCode,
    ]
  );

  return <formPrevProjContext.Provider value={value}>{children}</formPrevProjContext.Provider>;
};

export const useFormPrevProjContext = (): FormPrevProjContextProps =>
  useContext(formPrevProjContext);

export default FormPrevProjContextProvider;
